import React, { useState, useEffect } from 'react';
import './App.css';
import FeatureSelection from './components/FeatureSelection';
import ComponentSelection from './components/ComponentSelection';
import ContentMigration from './components/ContentMigration';
import HourCalculation from './components/HourCalculation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import logo from './logo.svg';
import { AuthProvider, useAuth } from './lib/AuthContext';
import { ProjectProvider, useProjects } from './lib/ProjectContext';
import Login from './components/Auth/Login';
import { Modal } from './components/ui/modal';
import Dashboard from './components/Projects/Dashboard';
import SaveProject from './components/Projects/SaveProject';
import NewEstimateModal from './components/Projects/NewEstimateModal';
import { Feature } from './lib/types';
import { Routes, Route, useNavigate, useParams, useLocation, Navigate } from 'react-router-dom';

interface AppContentProps {}

const ProjectCalculator: React.FC = () => {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { projects, currentProject, loadProjectBySlug } = useProjects();
  const [isLoading, setIsLoading] = useState<boolean>(!!projectSlug);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  useEffect(() => {
    // If user is not logged in, redirect to dashboard
    if (!currentUser) {
      navigate('/');
      return;
    }
    
    const loadProject = async () => {
      if (projectSlug) {
        try {
          // Find the project ID that corresponds to this slug
          await loadProjectBySlug(projectSlug);
          setIsLoading(false);
        } catch (error) {
          console.error('Error loading project from URL:', error);
          setIsLoading(false);
          // Redirect to dashboard if project not found
          navigate('/');
        }
      }
    };
    
    loadProject();
  }, [projectSlug, loadProjectBySlug, navigate, currentUser]);
  
  // If user is not logged in, redirect to dashboard
  if (!currentUser) {
    return null; // Return null as we're redirecting in the useEffect
  }
  
  if (isLoading) {
    return (
      <div className="container mx-auto py-20 text-center">
        <p>Loading project...</p>
      </div>
    );
  }
  
  return <AppContent initialView="calculator" />;
};

const AppContent: React.FC<{ initialView?: 'calculator' | 'dashboard' }> = ({ initialView = 'dashboard' }) => {
  const [selectedFeatures, setSelectedFeatures] = useState<Feature[]>([]);
  const [componentCount, setComponentCount] = useState<number>(10);
  const [isMigration, setIsMigration] = useState<boolean>(false);
  const [migrationType, setMigrationType] = useState<string>('wordpress-wordpress');
  const [postTypeCount, setPostTypeCount] = useState<number>(5);
  const [isContentUpload, setIsContentUpload] = useState<boolean>(false);
  const [pageCount, setPageCount] = useState<number>(5);
  const [contentUploadLevel, setContentUploadLevel] = useState<'light' | 'medium' | 'heavy'>('light');
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isNewEstimateModalOpen, setIsNewEstimateModalOpen] = useState(false);
  const [isEditTitleModalOpen, setIsEditTitleModalOpen] = useState(false);
  const [editProjectName, setEditProjectName] = useState('');
  const [editProjectDescription, setEditProjectDescription] = useState('');
  const { currentUser, signOut } = useAuth();
  const [view, setView] = useState<'calculator' | 'dashboard'>(currentUser ? initialView : 'dashboard');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const { currentProject, createNewProject, isCurrentUserOwner, saveProject, loadProject, updateProject } = useProjects();
  const navigate = useNavigate();
  const location = useLocation();

  // Update URL when current project changes
  useEffect(() => {
    if (currentProject?.slug && view === 'calculator') {
      // Update the URL without reloading the page
      window.history.replaceState(null, '', `/project/${currentProject.slug}`);
    }
  }, [currentProject, view]);

  // Redirect to dashboard if user logs out while in calculator view
  useEffect(() => {
    if (!currentUser && view === 'calculator') {
      setView('dashboard');
      navigate('/');
    }
  }, [currentUser, view, navigate]);

  const handleFeaturesChange = (features: Feature[]) => {
    // Simply set the selected features
    setSelectedFeatures(features);
  };

  const handleComponentCountChange = (count: number) => {
    setComponentCount(count);
  };

  const handleMigrationChange = (value: boolean) => {
    setIsMigration(value);
  };

  const handleMigrationTypeChange = (type: string) => {
    setMigrationType(type);
  };

  const handlePostTypeCountChange = (count: number) => {
    setPostTypeCount(count);
  };

  const handleContentUploadChange = (value: boolean) => {
    setIsContentUpload(value);
  };

  const handlePageCountChange = (count: number) => {
    setPageCount(count);
  };

  const handleContentUploadLevelChange = (level: 'light' | 'medium' | 'heavy') => {
    setContentUploadLevel(level);
  };

  // Load data from current project if available
  useEffect(() => {
    if (currentProject) {
      setSelectedFeatures(currentProject.selectedFeatures);
      setComponentCount(currentProject.componentCount);
      setEditProjectName(currentProject.name);
      setEditProjectDescription(currentProject.description || '');
      
      // Load migration data if available
      if (currentProject.isMigration !== undefined) {
        setIsMigration(currentProject.isMigration);
        setMigrationType(currentProject.migrationType || 'wordpress-wordpress');
        setPostTypeCount(currentProject.postTypeCount || 5);
      }
      
      // Load content upload data if available
      if (currentProject.isContentUpload !== undefined) {
        setIsContentUpload(currentProject.isContentUpload);
        setPageCount(currentProject.pageCount || 5);
        setContentUploadLevel(currentProject.contentUploadLevel || 'light');
      }
    }
  }, [currentProject]);

  // Log when selectedFeatures changes
  useEffect(() => {
    console.log('Selected features updated:', selectedFeatures.map(f => ({ id: f.id, name: f.name })));
  }, [selectedFeatures]);

  const handleLogout = async () => {
    try {
      await signOut();
      createNewProject(); // Reset current project
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleCreateNew = () => {
    // Reset any previous error/success messages
    setSaveError(null);
    setSaveSuccess(null);
    
    if (currentUser) {
      setIsNewEstimateModalOpen(true);
    } else {
      // If not logged in, prompt for login first
      setIsLoginModalOpen(true);
    }
  };
  
  const handleOpenProject = async (projectId: string) => {
    try {
      // First load the project by ID
      await loadProject(projectId);
      setView('calculator');
      
      // After loading, if the project has a slug, update the URL
      // But don't trigger another load
      if (currentProject?.slug) {
        window.history.replaceState(null, '', `/project/${currentProject.slug}`);
      }
    } catch (error) {
      console.error('Error opening project:', error);
    }
  };
  
  const handleEditTitleClick = () => {
    if (currentProject) {
      setEditProjectName(currentProject.name);
      setEditProjectDescription(currentProject.description || '');
      setIsEditTitleModalOpen(true);
    }
  };
  
  const handleSaveTitleChanges = async () => {
    if (!currentProject?.id || !editProjectName.trim()) return;
    
    try {
      await updateProject(currentProject.id, {
        name: editProjectName,
        description: editProjectDescription
      });
      setIsEditTitleModalOpen(false);
      setSaveSuccess('Project details updated successfully');
      
      // Display success message for 3 seconds
      setTimeout(() => {
        setSaveSuccess(null);
      }, 3000);
    } catch (error: any) {
      console.error('Error updating project details:', error);
      setSaveError(`Failed to update project details: ${error.message || 'Unknown error'}`);
    }
  };
  
  const handleSaveChanges = async () => {
    setSaveError(null);
    setSaveSuccess(null);
    
    if (!currentUser) {
      setIsLoginModalOpen(true);
      return;
    }
    
    // Don't allow changes if project is approved or rejected
    if (currentProject?.approved || currentProject?.rejected) {
      setSaveError(`Cannot modify a ${currentProject.approved ? 'approved' : 'rejected'} project. Please remove the ${currentProject.approved ? 'approval' : 'rejection'} status first.`);
      return;
    }
    
    try {
      // Log the features we're saving
      console.log('Saving features:', selectedFeatures.map(f => ({ id: f.id, name: f.name })));
      
      // If we have a current project, update it
      if (currentProject?.id) {
        await updateProject(currentProject.id, {
          selectedFeatures,
          componentCount,
          isMigration,
          migrationType,
          postTypeCount,
          isContentUpload,
          pageCount: 0,
          contentUploadLevel
        });
        setSaveSuccess('Project updated successfully');
        
        // Display success message for 3 seconds
        setTimeout(() => {
          setSaveSuccess(null);
        }, 3000);
      } else {
        // If no current project, prompt to create a new one
        setIsNewEstimateModalOpen(true);
      }
    } catch (error: any) {
      console.error('Error saving changes:', error);
      setSaveError(`Failed to save changes: ${error.message || 'Unknown error'}`);
    }
  };
  
  const handleToggleApproval = async () => {
    if (!currentProject?.id) return;
    
    setSaveError(null);
    setSaveSuccess(null);
    
    try {
      const newApprovalStatus = !currentProject.approved;
      
      // If approving, make sure to remove rejected status
      if (newApprovalStatus) {
        await updateProject(currentProject.id, {
          approved: newApprovalStatus,
          rejected: false
        });
      } else {
        await updateProject(currentProject.id, {
          approved: newApprovalStatus
        });
      }
      
      setSaveSuccess(newApprovalStatus ? "Project marked as approved!" : "Project approval removed.");
    } catch (error: any) {
      console.error('Error toggling approval status:', error);
      setSaveError(`Failed to update approval status: ${error.message || 'Unknown error'}`);
    }
  };
  
  const handleToggleRejection = async () => {
    if (!currentProject?.id) return;
    
    setSaveError(null);
    setSaveSuccess(null);
    
    try {
      const newRejectionStatus = !currentProject.rejected;
      
      // If rejecting, make sure to remove approved status
      if (newRejectionStatus) {
        await updateProject(currentProject.id, {
          rejected: newRejectionStatus,
          approved: false
        });
      } else {
        await updateProject(currentProject.id, {
          rejected: newRejectionStatus
        });
      }
      
      setSaveSuccess(newRejectionStatus ? "Project marked as rejected!" : "Project rejection removed.");
    } catch (error: any) {
      console.error('Error toggling rejection status:', error);
      setSaveError(`Failed to update rejection status: ${error.message || 'Unknown error'}`);
    }
  };

  const handleExportToGoogleSheets = () => {
    // Get component tier
    const getComponentTier = (count: number): number => {
      if (count < 5) return 1;
      if (count <= 10) return 2;
      if (count <= 15) return 3;
      return 4;
    };
    
    // Format hours display
    const formatHours = (min: number, max: number): string => {
      return min === max ? `${min}` : `${min}-${max}`;
    };
    
    // Calculate total hours
    const featuresMinHours = selectedFeatures.reduce((sum, feature) => sum + feature.minHours, 0);
    const featuresMaxHours = selectedFeatures.reduce((sum, feature) => sum + feature.maxHours, 0);
    const componentsHours = componentCount * 3;
    
    // Calculate migration hours
    const migrationHours = isMigration ? postTypeCount * 2 : 0;
    
    // Calculate content upload hours
    const getContentUploadHours = () => {
      if (!isContentUpload) return 0;
      switch (contentUploadLevel) {
        case 'light': return 4;
        case 'medium': return 8;
        case 'heavy': return 12;
        default: return 0;
      }
    };
    
    const contentUploadHours = getContentUploadHours();
    
    const totalMinHours = featuresMinHours + componentsHours + migrationHours + contentUploadHours;
    const totalMaxHours = featuresMaxHours + componentsHours + migrationHours + contentUploadHours;
    
    // Prepare data for Google Sheets
    const projectName = currentProject?.name || "New Project Estimate";
    const projectDescription = currentProject?.description || "";
    const componentTier = getComponentTier(componentCount);
    
    // Create CSV content with project name and description at the top
    let csvContent = `Project Name,${projectName}\n`;
    if (projectDescription) {
      csvContent += `Project Description,${projectDescription}\n`;
    }
    csvContent += "\n"; // Empty row for separation
    
    // Add component information
    csvContent += `Components (${componentCount}) - Tier ${componentTier},${componentsHours}\n`;
    
    // Add content upload if enabled
    if (isContentUpload) {
      const levelLabel = contentUploadLevel === 'light' ? 'Light' : contentUploadLevel === 'medium' ? 'Medium' : 'Heavy';
      csvContent += `Content Upload (${levelLabel}),${contentUploadHours}\n`;
    }
    
    // Add migration if enabled
    if (isMigration) {
      const isEcommerceMigration = migrationType === 'shopify-shopify' || migrationType === 'woocommerce-shopify';
      const typeLabel = isEcommerceMigration ? 'Product' : 'Post';
      csvContent += `Content Migration (${migrationType}): ${postTypeCount} ${typeLabel} Types,${migrationHours}\n`;
    }
    
    // Add features
    selectedFeatures.forEach(feature => {
      csvContent += `${feature.name},${formatHours(feature.minHours, feature.maxHours)}\n`;
    });
    
    // Add total
    csvContent += `Total,${formatHours(totalMinHours, totalMaxHours)}\n`;
    
    // Create a Blob with the CSV data
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    // Create a link element to download the CSV
    const link = document.createElement('a');
    const fileName = `${projectName.replace(/\s+/g, '_')}_estimate.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    
    // Append to the document, click it, and remove it
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Open Google Sheets in a new tab with instructions
    const googleSheetsUrl = 'https://docs.google.com/spreadsheets/u/0/create';
    window.open(googleSheetsUrl, '_blank');
    
    // Show success message with instructions
    setSaveSuccess('CSV downloaded. A new Google Sheet is opening. Go to File > Import to upload the CSV.');
    setTimeout(() => {
      setSaveSuccess(null);
    }, 8000);
  };

  const handleShareProject = () => {
    if (!currentProject?.slug) {
      setSaveError("This project needs to be saved before it can be shared.");
      return;
    }
    
    // Create the shareable URL with the project slug
    const baseUrl = window.location.origin;
    const shareUrl = `${baseUrl}/project/${currentProject.slug}`;
    
    // Copy to clipboard
    navigator.clipboard.writeText(shareUrl).then(
      () => {
        setSaveSuccess("Shareable link copied to clipboard!");
        
        // Display success message for 3 seconds
        setTimeout(() => {
          setSaveSuccess(null);
        }, 3000);
      },
      (err) => {
        console.error('Could not copy URL: ', err);
        setSaveError("Failed to copy link. Please try again.");
      }
    );
  };

  const handleCreateEstimate = async (title: string, description: string) => {
    createNewProject();
    setSaveError(null);
    setSaveSuccess(null);
    
    // If the user is logged in and provided a title, save the project immediately
    if (currentUser && title) {
      try {
        const projectId = await saveProject(
          title, 
          description, 
          selectedFeatures, 
          componentCount, 
          isMigration, 
          migrationType, 
          postTypeCount, 
          isContentUpload, 
          0, 
          contentUploadLevel
        );
        
        if (projectId) {
          await loadProject(projectId);
        }
      } catch (error: any) {
        console.error('Error creating new project:', error);
        setSaveError(`Failed to create estimate: ${error.message || 'Unknown error'}`);
      }
    }
    
    // Keep the user on the dashboard view
    setView('dashboard');
  };

  const renderCalculator = () => (
    <div className="container mx-auto flex flex-col flex-grow py-6 gap-6">
      {/* Notification area */}
      {(saveError || saveSuccess) && (
        <div className="w-full mb-4">
          {saveError && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md">
              {saveError}
            </div>
          )}
          {saveSuccess && (
            <div className="bg-green-50 border border-green-200 text-green-700 p-4 rounded-md">
              {saveSuccess}
            </div>
          )}
        </div>
      )}
      
      <div className="flex flex-col md:flex-row gap-6">
        {/* Left column */}
        <div className="w-full md:w-2/3">
          {/* Project Title */}
          <div className="mb-4 group relative">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-bold text-white">
                {currentProject?.name || "New Project Estimate"}
              </h2>
              {currentProject?.approved && (
                <Button variant="outline" size="sm" className="h-7 text-xs px-2 bg-green-100 text-green-800 border-green-200 hover:bg-green-100 hover:text-green-800">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                    <path d="M20 6L9 17l-5-5"></path>
                  </svg>
                  Approved
                </Button>
              )}
              {currentProject?.rejected && (
                <Button variant="outline" size="sm" className="h-7 text-xs px-2 bg-red-100 text-red-800 border-red-200 hover:bg-red-100 hover:text-red-800">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                  Rejected
                </Button>
              )}
              {currentProject?.id && (
                <button 
                  onClick={handleEditTitleClick}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:text-primary"
                  title="Edit project details"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </button>
              )}
            </div>
            {currentProject?.description && (
              <p className="text-sm text-white mt-1">
                {currentProject.description}
              </p>
            )}
          </div>
          
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Components Selection</CardTitle>
              <CardDescription>Select the number of components in your project</CardDescription>
            </CardHeader>
            <CardContent>
              <ComponentSelection 
                componentCount={componentCount}
                onComponentCountChange={handleComponentCountChange}
                disabled={currentProject?.approved || currentProject?.rejected}
              />
            </CardContent>
          </Card>
          
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Content Upload & Migration</CardTitle>
              <CardDescription>Configure content upload and migration options</CardDescription>
            </CardHeader>
            <CardContent>
              <ContentMigration 
                isMigration={isMigration}
                migrationType={migrationType}
                postTypeCount={postTypeCount}
                isContentUpload={isContentUpload}
                contentUploadLevel={contentUploadLevel}
                onMigrationChange={handleMigrationChange}
                onMigrationTypeChange={handleMigrationTypeChange}
                onPostTypeCountChange={handlePostTypeCountChange}
                onContentUploadChange={handleContentUploadChange}
                onContentUploadLevelChange={handleContentUploadLevelChange}
                disabled={currentProject?.approved || currentProject?.rejected}
              />
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Features Selection</CardTitle>
              <CardDescription>Select the features you want to include in your project</CardDescription>
            </CardHeader>
            <CardContent>
              <FeatureSelection 
                onFeaturesChange={handleFeaturesChange} 
                initialSelectedFeatures={selectedFeatures}
                disabled={currentProject?.approved || currentProject?.rejected}
              />
            </CardContent>
          </Card>
        </div>
        
        {/* Right column - sticky */}
        <div className="w-full md:w-1/3">
          <div className="sticky top-4 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Project Estimate</CardTitle>
                <CardDescription>Based on your selected features</CardDescription>
              </CardHeader>
              <CardContent>
                <HourCalculation 
                  selectedFeatures={selectedFeatures} 
                  componentCount={componentCount}
                  isMigration={isMigration}
                  postTypeCount={postTypeCount}
                  isContentUpload={isContentUpload}
                  contentUploadLevel={contentUploadLevel}
                />
                
                <div className="mt-6 space-y-3">
                  <Button 
                    className="w-full flex items-center justify-center gap-2"
                    onClick={handleExportToGoogleSheets}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="12" y1="18" x2="12" y2="12"></line>
                      <line x1="9" y1="15" x2="15" y2="15"></line>
                    </svg>
                    Open in Google Sheets
                  </Button>
                  
                  {currentProject?.id && (
                    <>
                      <Button 
                        className="w-full flex items-center justify-center gap-2" 
                        variant="outline"
                        onClick={handleShareProject}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                        </svg>
                        Share Project
                      </Button>
                      
                      <Button 
                        className="w-full" 
                        variant="outline"
                        onClick={handleSaveChanges}
                        disabled={currentProject?.approved || currentProject?.rejected}
                      >
                        Save Changes
                      </Button>
                      
                      <Button 
                        className="w-full flex items-center justify-center gap-2" 
                        variant={currentProject?.approved ? "destructive" : "secondary"}
                        onClick={handleToggleApproval}
                      >
                        {currentProject?.approved ? (
                          <>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M18 6L6 18M6 6l12 12"></path>
                            </svg>
                            Remove Approval
                          </>
                        ) : (
                          <>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M20 6L9 17l-5-5"></path>
                            </svg>
                            Mark as Approved
                          </>
                        )}
                      </Button>
                      
                      <Button 
                        className="w-full flex items-center justify-center gap-2" 
                        variant={currentProject?.rejected ? "outline" : "secondary"}
                        onClick={handleToggleRejection}
                      >
                        {currentProject?.rejected ? (
                          <>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M18 6L6 18M6 6l12 12"></path>
                            </svg>
                            Remove Rejection
                          </>
                        ) : (
                          <>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M18 6L6 18M6 6l12 12"></path>
                            </svg>
                            Mark as Rejected
                          </>
                        )}
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {selectedFeatures.length > 0 && !currentProject?.id && (
              <SaveProject 
                selectedFeatures={selectedFeatures}
                componentCount={componentCount}
                onLoginRequired={() => setIsLoginModalOpen(true)}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderDashboard = () => (
    <div className="container mx-auto py-6">
      <Dashboard 
        onCreateNew={handleCreateNew}
        onOpenProject={handleOpenProject}
        onLoginRequired={() => setIsLoginModalOpen(true)} 
      />
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-background dark">
      <header className="bg-primary text-primary-foreground p-4 shadow-md">
        <div className="container mx-auto flex items-center justify-between">
          <div 
            className="flex items-center gap-2 cursor-pointer" 
            onClick={() => {
              setView('dashboard');
              navigate('/');
            }}
            title="Go to Dashboard"
          >
            <img src={logo} alt="Logo" className="h-16 w-auto" />
            <h1 className="ml-4 text-2xl font-bold">Web Pricing Calculator</h1>
          </div>
          <div className="flex items-center gap-4">
            <nav>
              <ul className="flex gap-4">
                <li>
                  <Button 
                    variant={view === 'calculator' ? 'secondary' : 'ghost'} 
                    size="sm" 
                    onClick={() => {
                      if (currentUser) {
                        setView('calculator');
                      } else {
                        setIsLoginModalOpen(true);
                      }
                    }}
                  >
                    Estimator
                  </Button>
                </li>
                <li>
                  <Button 
                    variant={view === 'dashboard' ? 'secondary' : 'ghost'} 
                    size="sm" 
                    onClick={() => {
                      setView('dashboard');
                      // Reset URL to root when going to dashboard
                      navigate('/');
                    }}
                  >
                    Dashboard
                  </Button>
                </li>
              </ul>
            </nav>
            {currentUser ? (
              <div className="flex items-center gap-2">
                <span className="text-sm">{currentUser.email}</span>
                <button 
                  onClick={handleLogout}
                  className="p-1.5 hover:bg-white/10 rounded-full transition-colors" 
                  title="Logout"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <polyline points="16 17 21 12 16 7"></polyline>
                    <line x1="21" y1="12" x2="9" y2="12"></line>
                  </svg>
                </button>
              </div>
            ) : (
              <Button variant="secondary" size="sm" onClick={() => setIsLoginModalOpen(true)}>
                Login / Sign Up
              </Button>
            )}
          </div>
        </div>
      </header>
      
      {view === 'calculator' ? renderCalculator() : renderDashboard()}

      {/* Modals */}
      <Modal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)}>
        <Login onClose={() => setIsLoginModalOpen(false)} />
      </Modal>
      
      <NewEstimateModal
        isOpen={isNewEstimateModalOpen}
        onClose={() => setIsNewEstimateModalOpen(false)}
        onCreateEstimate={handleCreateEstimate}
      />
      
      {/* Edit Title Modal */}
      <Modal isOpen={isEditTitleModalOpen} onClose={() => setIsEditTitleModalOpen(false)}>
        <Card className="w-full max-w-md mx-auto">
          <CardHeader>
            <CardTitle>Edit Project Details</CardTitle>
            <CardDescription>Update your project name and description</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label htmlFor="editProjectName" className="block text-sm font-medium mb-1">
                  Project Name*
                </label>
                <input
                  type="text"
                  id="editProjectName"
                  value={editProjectName}
                  onChange={(e) => setEditProjectName(e.target.value)}
                  className="w-full p-2 border rounded-md text-black bg-white"
                  placeholder="Project Name"
                  autoFocus
                  required
                />
              </div>
              <div>
                <label htmlFor="editProjectDescription" className="block text-sm font-medium mb-1">
                  Description (optional)
                </label>
                <textarea
                  id="editProjectDescription"
                  value={editProjectDescription}
                  onChange={(e) => setEditProjectDescription(e.target.value)}
                  className="w-full p-2 border rounded-md text-black bg-white"
                  rows={3}
                  placeholder="Brief description of this project"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setIsEditTitleModalOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  type="button" 
                  onClick={handleSaveTitleChanges}
                  disabled={!editProjectName.trim()}
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </Modal>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <ProjectProvider>
        <Routes>
          <Route path="/" element={<AppContent />} />
          <Route 
            path="/project/:projectSlug" 
            element={
              <RequireAuth>
                <ProjectCalculator />
              </RequireAuth>
            } 
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ProjectProvider>
    </AuthProvider>
  );
}

// Protected route component that requires authentication
const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const location = useLocation();
  
  if (!currentUser) {
    // Redirect to the home page but remember where they were trying to go
    return <Navigate to="/" state={{ from: location }} replace />;
  }
  
  return <>{children}</>;
};

export default App;
